import { linkedTemplateIdsOf, photoGuideForAi, photoShotOf } from '../domain/photoGuide';
import type { V2Evidence } from '../domain/evidence';
import { AI_V2_LIMITS, AI_V2_PROTOCOL_VERSION, type AiV2AreaRequest, type AiV2ChecklistItem, type AiV2ErrorCode, type AiV2HouseContext, type AiV2Locale, type AiV2Photo, type AiV2Result, type AiV2WordingRequest, type AiV2WordingRow } from '../../supabase/functions/_shared/aiV2Protocol';
import { currentAccessToken, getAiTransport, getAiWordingTransport, type AiTransportFailure } from '../backend/aiClient';
import { prepareImageForAi } from '../backend/aiImagePrep';
import { mergeAppendedObservations } from '../domain/aiObservationMerge';
import {
  createAiObservation,
  createAiRun,
  observationBelongsToRow,
  observationIsUnclassified,
  withObservationFiledToItem,
  withObservationLimitation,
  withObservationNoted,
  withObservationOtherFinding,
  withRunDone,
  withRunFailed,
  withRunGateLog,
  type AiGateLogEntry,
  type AiObservationContent,
  type V2AiObservation,
  type V2AiRun
} from '../domain/aiObservation';
import {
  gateAiResults,
  gateSurfaces,
  gateWithoutPacket,
  presentationOfScope,
  scopeOfDecision,
  scopePacketFor,
  snapshotPropertyFactsOf,
  type GateDecision,
  type GateInput,
  type GateInspectorAction,
  type SuggestionPresentation
} from '../domain/scopeGate';
import { CHECKLIST_TEMPLATE_EN_US } from '../domain/checklistTemplates.en-US';
import { templateRowScope } from '../domain/checklistTemplates';
import { itemDescriptorAnswer, type DescriptorKey } from '../domain/descriptors';
import { newId } from '../domain/ids';
import { aiPolicyBlockOf, contextKindOf, nonClientTestMarkerOf, type AiPolicyBlock } from '../domain/inspectionContext';
import type { JurisdictionCode } from '../domain/jurisdiction';
import { canCarryLimitedPortions, isInspectionWorkable, type ItemReportSentence, type ItemStatus, type V2Area, type V2ChecklistItem, type V2Inspection } from '../domain/inspection';
import { isResponsibleTrade } from '../domain/responsibleTrade';
import { applyInspectionRules, referralForStructuralConcern, stripInvasiveInstructions, tradeForTemplateRow } from '../domain/inspectionRules';
import { stripProhibitedSentences } from '../domain/wordingGuard';
import { currentLocale, translate, type MessageKey, type MessageParams } from '../i18n/i18n';
import { areaDisplayName, tradeLabel } from '../i18n/labels';
import { storedSentenceCurrent, wordingBasisOf, type WordingFacts } from '../report/rowWording';
import { accountGuard } from '../persistence/accountScope';
import { getMedia, putRecords, type RecordDelete, type RecordWrite } from '../persistence/v2Database';
import { applyServerQuota } from './billingStore';
import { createStore } from './createStore';
import { evidenceStore, type EvidenceState } from './evidenceStore';
import { addFindingAction, findingStore } from './findingStore';
import { addLimitedPortionAction, areasOf, assertInspectionUnlocked, inspectionStore, itemsOf, prepareAiDefectDefaults, prepareItemEvidenceSuggestions, setItemReportSentenceAction } from './inspectionStore';
import { areaScanPhotos } from './photoPool';
import { readInspectorProfile } from './preferences';
import { sessionStore } from './sessionStore';

// AI OBSERVATIONS state (Final Owner Decision 2026-09-14, ratification CA-1 … CA-8; PP-21 unchanged).
// Hard rules kept here by construction:
//   - AI never writes an ItemStatus and never creates a finding by itself. An observation is evidence
//     shown on the row it belongs to (③); the inspector's result (④) is the only decision (CA-8 / CA-11);
//   - an observation the AI could not match to a row waits in the area's unclassified list until the
//     inspector files it into a row, records it as an other finding, records the limitation, or ignores
//     it (CA-5). Nothing here ever adds a checklist row;
//   - the photos an observation rests on are linked to its row automatically — never one the inspector
//     removed from that row (CA-7 / §9.1 / §9.2);
//   - a failed / offline / missing AI never blocks anything: every guard here only refuses the AI
//     request, the manual flow is untouched;
//   - runs and observations are persisted (the trail), the live phase is in memory.
export type AiPhase = 'preparing' | 'uploading' | 'analyzing';

export interface LiveRun {
  runId: string;
  areaId: string;
  phase: AiPhase;
  uploadPct: number;
  startedAt: number;
}

export interface AiState {
  hydrated: boolean;
  runs: V2AiRun[];
  observations: V2AiObservation[];
  // In-flight area analyses keyed by area id.
  live: Record<string, LiveRun>;
  // Report-wording calls in flight, keyed by area id.
  wordingLive: Record<string, boolean>;
}

export const aiStore = createStore<AiState>({ hydrated: false, runs: [], observations: [], live: {}, wordingLive: {} });

export function resetAiStoreForTests(): void {
  aiStore.set(() => ({ hydrated: false, runs: [], observations: [], live: {}, wordingLive: {} }));
}

export function applyAiSnapshot(runs: V2AiRun[], observations: V2AiObservation[]): void {
  aiStore.set((prev) => ({ ...prev, hydrated: true, runs: [...runs], observations: [...observations] }));
}

export function removeAiOfInspectionLocal(inspectionId: string): void {
  aiStore.set((prev) => ({
    ...prev,
    runs: prev.runs.filter((r) => r.inspectionId !== inspectionId),
    observations: prev.observations.filter((s) => s.inspectionId !== inspectionId)
  }));
}

function upsertRun(run: V2AiRun): void {
  aiStore.set((prev) => ({ ...prev, runs: prev.runs.some((r) => r.id === run.id) ? prev.runs.map((r) => (r.id === run.id ? run : r)) : [...prev.runs, run] }));
}

function upsertObservations(list: V2AiObservation[]): void {
  if (list.length === 0) return;
  aiStore.set((prev) => {
    const byId = new Map(list.map((s) => [s.id, s] as const));
    const kept = prev.observations.map((s) => byId.get(s.id) ?? s);
    const known = new Set(prev.observations.map((s) => s.id));
    return { ...prev, observations: [...kept, ...list.filter((s) => !known.has(s.id))] };
  });
}

function setLive(key: string, live: LiveRun | null): void {
  aiStore.set((prev) => {
    const next = { ...prev.live };
    if (live) next[key] = live;
    else delete next[key];
    return { ...prev, live: next };
  });
}

async function persist(writes: RecordWrite[], deletes: RecordDelete[] = []): Promise<void> {
  await putRecords(writes, deletes);
}

// ---------- selectors ----------

export function observationsOfArea(state: AiState, areaId: string): V2AiObservation[] {
  return state.observations.filter((s) => s.areaId === areaId).sort((a, b) => a.createdAt - b.createdAt || a.sortOrder - b.sortOrder);
}

// NY F1 Round 4 / OD-13. One AI result is one of three presentations; the unclassified list renders
// each with the actions its gate decision allows:
//   standard         an ordinary observation
//   scopeReminder    "Outside the New York inspection scope" — record as other finding · noted
//   limitationPrompt "part of an in-scope component was not observed" — record the limitation on the row
export function observationPresentationOf(observation: V2AiObservation): SuggestionPresentation {
  return presentationOfScope(observation.scope);
}

// The observations shown in a row's ③ (CA-3 / CA-8): the ones the AI matched to the row and the ones
// the inspector filed into it.
export function observationsOfItem(state: AiState, itemId: string): V2AiObservation[] {
  return state.observations.filter((s) => s.itemId === itemId && observationBelongsToRow(s)).sort((a, b) => a.createdAt - b.createdAt || a.sortOrder - b.sortOrder);
}

// What the screen lists for a row (2026-10-28, device walkthrough A6: 「未见明显异常 · 置信度低」 sat beside three
// defect lines of the same row). Once any observation of the row concluded a defect, its "no visible defect"
// lines are noise and are not shown; without a defect line, they are. The trail keeps every observation.
export function observationsShownForItem(state: AiState, itemId: string): V2AiObservation[] {
  const all = observationsOfItem(state, itemId);
  return all.some((o) => o.verdict === 'defect') ? all.filter((o) => o.verdict !== 'noVisibleDefect') : all;
}

// Why the area page keeps a row open although it has a result (2026-10-28, device walkthrough A2): the AI saw
// a defect on it, or asked the inspector something about it. null = nothing of the AI waits on this row.
export function aiAttentionOfItem(state: AiState, itemId: string): 'defect' | 'question' | null {
  const all = observationsOfItem(state, itemId);
  if (all.some((o) => o.verdict === 'defect')) return 'defect';
  if (all.some((o) => o.type === 'needsClarification' && (o.question ?? '').trim().length > 0)) return 'question';
  return null;
}

export function itemHasObservations(state: AiState, itemId: string): boolean {
  return state.observations.some((s) => s.itemId === itemId && observationBelongsToRow(s));
}

// The area's unclassified list (CA-5): every open observation the AI could not match to a row —
// ordinary, scope reminders and limitation prompts alike.
export function unclassifiedObservationsOfArea(state: AiState, areaId: string): V2AiObservation[] {
  return observationsOfArea(state, areaId).filter(observationIsUnclassified);
}

// The report-language text of a row's observations, as the wording basis and the wording request read them.
// 2026-10-27 (device walkthrough: a 无异常 row's sentence read "no deficiency was observed" and then described
// the crack the AI saw): on a row the inspector marked 无异常, the observations that concluded a defect are
// left out — the inspector overruled them, and the sentence must not carry them.
export function observationTextsOfItem(state: AiState, itemId: string, options: { rowStatus?: ItemStatus } = {}): string[] {
  return observationsOfItem(state, itemId)
    .filter((o) => !(options.rowStatus === 'checkedOk' && o.verdict === 'defect'))
    .map((o) => (o.observation.trim().length > 0 ? o.observation : o.headline));
}

// What the AI scan card says once a run is done (CA-2): photos analyzed · rows with an observation ·
// unclassified observations still waiting.
export interface AreaScanSummary {
  run: V2AiRun;
  photos: number;
  rows: number;
  unclassified: number;
}

// `photos` counts every photo of the area a finished analysis has seen, across all its runs and without
// double counting (2026-10-28, device walkthrough A8: 42 photos analyzed in two batches read 「已分析 18 张」 —
// the last batch only — which invites a paid 「全部重新分析」). Given the area's current photos, a photo no
// longer in the area is not counted.
export function areaScanSummaryOf(state: AiState, areaId: string, currentPhotoIds?: readonly string[]): AreaScanSummary | null {
  const run = latestRunOfArea(state, areaId);
  if (!run || run.status !== 'done') return null;
  const ofArea = observationsOfArea(state, areaId);
  const rows = new Set(ofArea.filter(observationBelongsToRow).map((o) => o.itemId as string)).size;
  const analyzed = analyzedPhotoIdsOfArea(state, areaId);
  const photos = currentPhotoIds ? new Set(currentPhotoIds.filter((id) => analyzed.has(id))).size : analyzed.size;
  return { run, photos, rows, unclassified: ofArea.filter(observationIsUnclassified).length };
}

// What the inspector may do with this observation. The gate decides it; an observation without a
// gate decision keeps the ordinary actions.
const LEGACY_ACTIONS: readonly GateInspectorAction[] = Object.freeze(['findingOnItem', 'otherFinding', 'acknowledge', 'ignore']);

export function observationAllowsAction(observation: V2AiObservation, action: GateInspectorAction): boolean {
  return observation.scope === null ? LEGACY_ACTIONS.includes(action) : observation.scope.allowedActions.includes(action);
}

// 归入现有检查项 / 记为其他问题 (CA-5): an in-scope observation the AI could not match may be filed into
// any row the inspector chooses or recorded as an other finding; a scope reminder or a limitation
// prompt never can (they keep their own two actions).
export function observationCanBeFiled(observation: V2AiObservation): boolean {
  if (observationPresentationOf(observation) !== 'standard') return false;
  return observationAllowsAction(observation, 'findingOnItem') || observationAllowsAction(observation, 'otherFinding') || observationAllowsAction(observation, 'addItem');
}

export function observationsOfInspection(state: AiState, inspectionId: string): V2AiObservation[] {
  return state.observations.filter((s) => s.inspectionId === inspectionId);
}

export function latestRunOfArea(state: AiState, areaId: string): V2AiRun | null {
  const runs = state.runs.filter((r) => r.areaId === areaId).sort((a, b) => b.startedAt - a.startedAt);
  return runs[0] ?? null;
}

// Every photo a finished analysis of this area has already seen (Owner decision list 2026-10-19 item
// 29: from the second analysis on, only new photos are analyzed; what was analyzed is kept).
export function analyzedPhotoIdsOfArea(state: AiState, areaId: string): Set<string> {
  const seen = new Set<string>();
  for (const run of state.runs) if (run.areaId === areaId && run.status === 'done') for (const id of run.photoIds) seen.add(id);
  return seen;
}

export function liveRunOfArea(state: AiState, areaId: string): LiveRun | null {
  return state.live[areaId] ?? null;
}

// ---------- PP-21: the cloud-AI policy gate (NY F1 Round 1) ----------
//
// F1 blocks cloud AI for a real New York client engagement pending legal counsel (LR-02 / LR-19 /
// LR-20). The block is implemented in THREE layers so no entry point can slip through:
//   1  declarative — aiUnavailableReason() below, so the button is visibly disabled with a real
//      explanation instead of a silent no-op;
//   2  action      — an early return in runAreaAnalysisAction, which stops a programmatic caller
//      that never touched the UI;
//   3  chokepoint  — inside executeRun(), immediately BEFORE getAiTransport()(...), the one and
//      only line in the whole product where a request leaves the device.
// Layer 3 is what actually guarantees the property; layers 1 and 2 exist so the inspector sees an
// honest state instead of a failure. tests/guards/aiEntryPoints.guard.test.ts pins the shape.
function inspectorLicensedState(): JurisdictionCode | null {
  return readInspectorProfile().licensedState;
}

// NY F1 Round 4 adds the fourth reason a request must not be made: this inspection has NO New York
// scope packet (frozen plan §6). The packet is frozen at agreement signing, and without it no AI
// result could be admitted anyway — so the request is refused BEFORE anything leaves the device
// rather than sent and then discarded. A legal-review block keeps its own existing reason.
// Owner 2026-10-07: a Demo needs no signature for AI (src/domain/scopeGate.ts scopePacketFor), so the
// only thing that can leave a Demo without a packet is a property that is not an asserted New York
// property — `blockedNoScopeState` says exactly that instead of asking for a signature.
export type AiScopeBlock = 'blockedNoScope' | 'blockedNoScopeState';

export type AiRunBlock = AiPolicyBlock | AiScopeBlock;

export function aiPolicyBlockFor(inspection: V2Inspection): AiRunBlock | null {
  const policy = aiPolicyBlockOf({ inspection, inspectorState: inspectorLicensedState() });
  if (policy) return policy;
  const packet = scopePacketFor(inspection);
  if (packet.kind === 'ready') return null;
  if (packet.reason === 'legalReviewBlock') return 'blockedLegalReview';
  return contextKindOf(inspection) === 'nonClientTest' ? 'blockedNoScopeState' : 'blockedNoScope';
}

// What the inspector does to clear an AI block, right from the AI card (Owner 2026-10-07: 「去签协议」):
// sign the agreement (a signature, the client's AI consent, an outdated copy — all on the document),
// or choose the property state of a Demo. Every other block is not the inspector's to clear here.
export function aiBlockFix(reason: string | null): 'agreement' | 'state' | null {
  switch (reason) {
    case 'blockedAgreementUnsigned':
    case 'blockedAgreementOutdated':
    case 'blockedNotConsented':
    case 'blockedNoScope':
      return 'agreement';
    case 'blockedNoScopeState':
      return 'state';
    default:
      return null;
  }
}

// ---------- availability (why the Analyze button is disabled) ----------

export type AiUnavailableReason =
  | 'noPhotos'
  | 'offline'
  | 'notSignedIn'
  | 'notConfigured'
  | 'locked'
  | 'busy'
  // The AI allowance this account (or this inspection) has left is gone — Owner decisions D-4 / D-5
  // (2026-09-11). The server has the final word; this is what the card knows before the tap.
  | 'quotaExhausted'
  | AiRunBlock;

export function aiUnavailableReason(input: {
  photos: number;
  online: boolean;
  signedIn: boolean;
  configured: boolean;
  locked: boolean;
  busy: boolean;
  // PP-21 / PP-20 / jurisdiction / no scope packet. Checked FIRST: a policy block is not a transient
  // condition and must never be reported as "add photos" or "you are offline".
  policyBlock?: AiRunBlock | null;
  // Billing (src/state/billingStore.ts owns it; this module never imports it): reported before
  // "add photos" so the inspector is not asked to prepare a run that cannot be paid for.
  quotaBlock?: 'quotaExhausted' | null;
}): AiUnavailableReason | null {
  if (input.policyBlock) return input.policyBlock;
  if (input.locked) return 'locked';
  if (input.busy) return 'busy';
  if (input.quotaBlock) return input.quotaBlock;
  if (input.photos === 0) return 'noPhotos';
  if (!input.online) return 'offline';
  if (!input.configured) return 'notConfigured';
  if (!input.signedIn) return 'notSignedIn';
  return null;
}

// ---------- request assembly (pure; exported for tests) ----------

export type AiFailureCode = AiV2ErrorCode | AiTransportFailure | AiUnavailableReason | 'imageFailed' | 'notFound';

function reportLocaleOf(inspection: V2Inspection): AiV2Locale {
  return inspection.reportLanguage === 'zh-CN' ? 'zh-CN' : 'en-US';
}

function checklistContext(items: readonly V2ChecklistItem[]): AiV2ChecklistItem[] {
  return items.map((item) => {
    const en = item.templateId ? CHECKLIST_TEMPLATE_EN_US[item.templateId] : undefined;
    // templateId (2026-10-19): lets the server list what a sound house has at this row (「该有而没有」).
    // trade (2026-10-19, Owner: 「一定要写明是哪类专业人员」): the rules library's professional for the row, so the
    // model names that kind in its recommendation; null = the row names none; omitted when the row is unknown.
    const trade = tradeForTemplateRow(item.templateId);
    return { id: item.id, title: item.title, titleEn: en && en.title !== item.title ? en.title : null, hint: item.hint, status: item.status, templateId: item.templateId ?? null, ...(trade === undefined ? {} : { trade }) };
  });
}

// 2026-10-18 (Owner: the AI invented a water heater's TPR valve and a bedroom heater — it was told
// the room kind and the year built and nothing else): the house systems the inspector RECORDED, read
// from the property facts and from the HVAC / water-heater rows of the WHOLE inspection (they sit in
// other areas than the one being analyzed). A fact nobody recorded travels as null and the prompt
// says "not recorded"; nothing is inferred from the property type, the year or the photos.
const HOUSE_CODE_MAX_CHARS = 80;

function descriptorFact(items: readonly V2ChecklistItem[], templateId: string, key: DescriptorKey): string | null {
  for (const item of items) {
    if (item.templateId !== templateId) continue;
    const answer = itemDescriptorAnswer(item, key);
    if (!answer || answer.value.trim().length === 0) continue;
    const detail = answer.detail.trim();
    return `${answer.value.trim()}${detail.length > 0 ? ` (${detail})` : ''}`.slice(0, HOUSE_CODE_MAX_CHARS);
  }
  return null;
}

export function houseContextOf(inspection: V2Inspection, inspectionItems: readonly V2ChecklistItem[]): AiV2HouseContext | null {
  const p = inspection.property;
  const house: AiV2HouseContext = {
    heatingFuel: p.heatingFuel,
    heatingEquipment: descriptorFact(inspectionItems, 'hvac-heating-unit', 'heatingEquipmentType'),
    heatingDistribution: descriptorFact(inspectionItems, 'hvac-distribution', 'heatingDistribution'),
    waterHeaterEnergySource: descriptorFact(inspectionItems, 'wh-data-plate', 'waterHeaterEnergySource'),
    waterSupplySource: p.waterSupplySource,
    wasteDisposalSource: p.wasteDisposalSource,
    basementType: p.basementType,
    basementFinish: p.basementFinish
  };
  return Object.values(house).some((v) => v !== null) ? house : null;
}

export function buildAreaRequestContext(
  inspection: V2Inspection,
  area: V2Area,
  items: readonly V2ChecklistItem[],
  inspectionItems: readonly V2ChecklistItem[] = inspectionStore.get().items.filter((i) => i.inspectionId === inspection.id)
): Pick<AiV2AreaRequest, 'protocolVersion' | 'uiLocale' | 'reportLocale' | 'area' | 'checklist' | 'inspectionId'> {
  return {
    protocolVersion: AI_V2_PROTOCOL_VERSION,
    uiLocale: currentLocale(),
    reportLocale: reportLocaleOf(inspection),
    // Which inspection this analysis belongs to, so the server charges it to that inspection's own
    // AI allowance when a single purchase paid for it (Owner decision 2026-09-11 D-5).
    inspectionId: inspection.id,
    area: {
      areaId: area.id,
      areaName: areaDisplayName(currentLocale(), area),
      areaNameEn: areaDisplayName('en-US', area),
      areaKind: area.kind,
      floor: area.floor,
      propertyType: inspection.property.propertyType,
      yearBuilt: inspection.property.yearBuilt,
      house: houseContextOf(inspection, inspectionItems)
    },
    checklist: checklistContext(items)
  };
}

export function resultToContent(result: AiV2Result): AiObservationContent {
  return {
    type: result.type,
    itemId: result.itemId,
    title: result.title,
    headline: result.headline,
    observation: result.observation,
    concern: result.concern,
    guidance: result.guidance,
    checkSteps: result.checkSteps,
    recommendation: result.recommendation,
    responsibleTrade: isResponsibleTrade(result.responsibleTrade) ? result.responsibleTrade : null,
    observationUi: result.observationUi ?? null,
    recommendationUi: result.recommendationUi ?? null,
    confidence: result.confidence,
    // 2026-10-18: the closed conclusion; the protocol check already derived one for an older server.
    verdict: result.verdict ?? null,
    question: result.question,
    scopeNote: result.scopeNote,
    photoIds: result.photoIds
  };
}

// 2026-10-19 (Owner item 31): a photo taken for a photo-checklist shot says which shot it is, before the
// inspector's own note — the AI then knows what the photo was meant to show.
function photoNoteForAi(record: V2Evidence): string {
  const area = record.areaId ? inspectionStore.get().areas.find((a) => a.id === record.areaId) : undefined;
  const shot = area ? photoShotOf(area.kind, record.shot) : null;
  return shot ? `[${shot.en}] ${record.caption}`.trim() : record.caption;
}

// Photos → wire form (downscaled). Unreadable photos are skipped with a warning; none readable = failure.
// Shared with the advanced job store (src/state/aiJobsStore.ts): one preparation, one size rule.
export async function preparePhotosForAi(photoIds: readonly string[], onProgress?: (done: number, total: number) => void): Promise<{ photos: AiV2Photo[]; skipped: string[] }> {
  const evidenceById = new Map(evidenceStore.get().evidence.map((e) => [e.id, e] as const));
  const photos: AiV2Photo[] = [];
  const skipped: string[] = [];
  let done = 0;
  for (const id of photoIds) {
    const record = evidenceById.get(id);
    let prepared = null;
    if (record) {
      try {
        const blob = await getMedia(id, 'full');
        prepared = blob ? await prepareImageForAi(blob) : null;
      } catch {
        prepared = null;
      }
    }
    if (record && prepared && prepared.base64.length <= AI_V2_LIMITS.maxPhotoBase64Chars) {
      photos.push({ id, mediaType: prepared.mediaType, base64: prepared.base64, note: photoNoteForAi(record), capturedAt: record.createdAt });
    } else skipped.push(id);
    done += 1;
    onProgress?.(done, photoIds.length);
  }
  return { photos, skipped };
}

export type AnalysisOutcome = { ok: true; run: V2AiRun; observations: V2AiObservation[]; warnings: string[] } | { ok: false; code: AiFailureCode; run: V2AiRun | null };

// ---------- the Scope Gate in the AI flow (NY F1 Round 4; plan §9 insertion point) ----------
//
// Between the provider's answer and `createAiObservation(...)`: every result is classified against
// the inspection's frozen New York packet before it can become an observation. Nothing here writes an
// ItemStatus, nothing here creates a finding, and no result is thrown away — what the gate does not
// surface is kept in the run's gate log for audit (AI policy §3 outputs; OD-13).
export function gateAiContentForArea(inspection: V2Inspection, area: V2Area, inputs: readonly GateInput[]): GateDecision[] {
  const packet = scopePacketFor(inspection);
  if (packet.kind !== 'ready') return gateWithoutPacket(inputs, packet.reason);
  const state = inspectionStore.get();
  return gateAiResults(inputs, {
    packet: packet.packet,
    areaKind: area.kind,
    areaItems: itemsOf(state, area.id),
    inspectionItems: state.items.filter((i) => i.inspectionId === inspection.id),
    areaKinds: areasOf(state, inspection.id).filter((a) => a.enabled).map((a) => a.kind),
    propertyFacts: snapshotPropertyFactsOf(inspection)
  });
}

function gateLogEntryOf(decision: GateDecision, original: AiObservationContent, observationId: string | null): AiGateLogEntry {
  return {
    resultId: decision.resultId,
    observationId,
    action: decision.action,
    reason: decision.reason,
    subjectId: decision.subjectId,
    disposition: decision.disposition,
    legalStatus: decision.legalStatus,
    suppressed: decision.suppressed,
    ruleIds: [...decision.ruleIds],
    authorityVersion: decision.authorityVersion,
    prohibitedHits: decision.prohibitedHits.map((hit) => ({ ...hit })),
    original: { type: original.type, itemId: original.itemId, title: original.title, headline: original.headline, observation: original.observation, recommendation: original.recommendation }
  };
}

// The inspection as it is RIGHT NOW (the run may have taken seconds); falls back to the one the
// action started with, which is only used to resolve the packet and the property facts.
function currentInspection(inspection: V2Inspection): V2Inspection {
  return inspectionStore.get().inspections.find((i) => i.id === inspection.id) ?? inspection;
}

interface RunContext {
  inspection: V2Inspection;
  area: V2Area;
  items: V2ChecklistItem[];
}

function contextFor(areaId: string): RunContext | null {
  const state = inspectionStore.get();
  const area = state.areas.find((a) => a.id === areaId);
  if (!area) return null;
  const inspection = state.inspections.find((i) => i.id === area.inspectionId);
  if (!inspection) return null;
  return { inspection, area, items: itemsOf(state, areaId) };
}

async function executeRun(
  liveKey: string,
  run: V2AiRun,
  build: (photos: AiV2Photo[]) => AiV2AreaRequest,
  now: number
): Promise<{ ok: true; results: AiV2Result[]; summary: string; run: V2AiRun; warnings: string[] } | { ok: false; code: AiFailureCode; run: V2AiRun }> {
  const startedAt = Date.now();
  // ACCOUNT ISOLATION: the answer may take a minute. If the inspector signed out meanwhile — and
  // somebody else signed in — this run, its observations and the allowance it reports are the
  // previous account's: nothing of them is written, shown or counted from that moment on.
  const sameAccount = accountGuard();
  await persist([{ store: 'aiRuns', value: run }]);
  upsertRun(run);
  setLive(liveKey, { runId: run.id, areaId: run.areaId, phase: 'preparing', uploadPct: 0, startedAt });
  const fail = async (code: AiFailureCode): Promise<{ ok: false; code: AiFailureCode; run: V2AiRun }> => {
    const failed = withRunFailed(run, code, Date.now());
    if (!sameAccount()) return { ok: false, code, run: failed };
    try {
      await persist([{ store: 'aiRuns', value: failed }]);
    } catch {
      // the failure is still shown from memory
    }
    upsertRun(failed);
    setLive(liveKey, null);
    return { ok: false, code, run: failed };
  };
  const { photos, skipped } = await preparePhotosForAi(run.photoIds);
  if (photos.length === 0) return fail('imageFailed');
  const request = build(photos);
  const token = await currentAccessToken();
  // The token is the CURRENT session's: one account's photos never travel under another's sign-in.
  if (!token || !sameAccount()) return fail('notSignedIn');
  // LAYER 3 — THE CHOKEPOINT (PP-21 / PP-20 / jurisdiction). The next statement is the only place
  // in this product where a request carrying an inspection's photos and notes leaves the device.
  // Nothing reaches the provider without passing here, and an inspection that cannot be resolved
  // is treated as a real client engagement, not waved through.
  const subject = inspectionStore.get().inspections.find((i) => i.id === run.inspectionId) ?? null;
  const policyBlock = subject ? aiPolicyBlockFor(subject) : 'blockedRealClient';
  if (policyBlock) return fail(policyBlock);
  const result = await getAiTransport()(request, {
    accessToken: token,
    onPhase: (phase, pct) => {
      if (sameAccount()) setLive(liveKey, { runId: run.id, areaId: run.areaId, phase, uploadPct: pct, startedAt });
    }
  });
  if (!sameAccount()) return fail('notSignedIn');
  // The server answers every AI request with the allowance that is left (Owner decision 2026-09-11
  // X-7). Applying it here keeps the plan page and the AI card on the server's number, including on
  // a refusal — which is how the AI card can say "allowance used up" instead of a generic failure.
  if (result.kind === 'response' && result.response.quota) applyServerQuota(result.response.quota);
  if (result.kind === 'failed') return fail(result.code);
  if (!result.response.ok) return fail(result.response.code);
  const response = result.response;
  if (response.kind !== 'area') return fail('malformedResponse');
  const warnings = [...response.warnings];
  if (skipped.length > 0) warnings.push(`skipped:${skipped.length}`);
  const done = withRunDone(
    run,
    response.summary,
    { model: response.usage.model, inputTokens: response.usage.inputTokens, outputTokens: response.usage.outputTokens, durationMs: Date.now() - startedAt, requestId: response.usage.requestId },
    Math.max(now, Date.now())
  );
  return { ok: true, results: response.results, summary: response.summary, run: done, warnings };
}

// ---------- area analysis ----------

// `append` (Owner device test 2026-10-05, 「AI 分析照片池」): this run is one part of the whole-pool
// analysis and covers photos no earlier scan of the area saw, so it ADDS its observations instead of
// replacing the area's earlier ones.
export async function runAreaAnalysisAction(areaId: string, options: { photoIds?: string[]; now?: number; append?: boolean } = {}): Promise<AnalysisOutcome> {
  const now = options.now ?? Date.now();
  const ctx = contextFor(areaId);
  if (!ctx) return { ok: false, code: 'notFound', run: null };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, code: 'locked', run: null };
  // LAYER 2 (PP-21): refuse before any run record, any photo preparation and any network work.
  const areaPolicyBlock = aiPolicyBlockFor(ctx.inspection);
  if (areaPolicyBlock) return { ok: false, code: areaPolicyBlock, run: null };
  if (aiStore.get().live[areaId]) return { ok: false, code: 'busy', run: null };
  // The area's own photos plus the pool photos filed into it — what the area's AI card shows.
  const areaPhotos = areaScanPhotos(evidenceStore.get(), inspectionStore.get(), findingStore.get(), areaId);
  const photoIds = (options.photoIds ?? areaPhotos.map((e) => e.id)).slice(0, AI_V2_LIMITS.maxPhotos);
  if (photoIds.length === 0) return { ok: false, code: 'noPhotos', run: null };
  if (sessionStore.get().phase !== 'signedIn') return { ok: false, code: 'notSignedIn', run: null };
  const run = createAiRun({ inspectionId: ctx.inspection.id, areaId, photoIds, scanNote: ctx.area.scanNote, contextMarker: nonClientTestMarkerOf(ctx.inspection) }, now);
  // 2026-10-19 (Owner item 31): the photo checklist's answers — parts the inspector said are not here, and
  // the usual views nobody answered — go with the request as the inspector's own facts.
  const photoGuide = photoGuideForAi(ctx.area, evidenceStore.get().evidence, (templateId) => ctx.items.find((i) => i.templateId === templateId)?.id ?? null, linkedTemplateIdsOf(ctx.items, areaId));
  const outcome = await executeRun(areaId, run, (photos) => ({ ...buildAreaRequestContext(ctx.inspection, ctx.area, ctx.items), kind: 'area', requestId: run.id, scanNote: ctx.area.scanNote, photos, ...(photoGuide ? { photoGuide } : {}) }), now);
  if (!outcome.ok) return { ok: false, code: outcome.code, run: outcome.run };
  // A new analysis REPLACES the observations of the previous one (CA-2 / D-CA-4): what the inspector
  // already did with them — a filed row's photo links, an other finding, a recorded limitation, a
  // result on a row — stands on its own record and is untouched. A part of the pool analysis adds.
  const stale = options.append === true ? [] : aiStore.get().observations.filter((s) => s.areaId === areaId);
  const finishedAt = outcome.run.finishedAt ?? now;
  // THE SCOPE GATE (plan §9): classify first, then build the observations from what it admitted.
  const inputs: GateInput[] = outcome.results.map((r) => ({ id: r.id, content: resultToContent(r) }));
  const decisions = gateAiContentForArea(currentInspection(ctx.inspection), ctx.area, inputs);
  const observations: V2AiObservation[] = [];
  const gateLog: AiGateLogEntry[] = [];
  decisions.forEach((decision, index) => {
    // THE RULES LIBRARY: the professional and the check method come from the row, never from the model.
    const surfaced = gateSurfaces(decision) && decision.content.type !== 'noAction' ? createAiObservation(outcome.run, applyInspectionRules(decision.content, ctx.items, decision.subjectId), index, finishedAt, scopeOfDecision(decision)) : null;
    if (surfaced) observations.push(surfaced);
    gateLog.push(gateLogEntryOf(decision, inputs[index].content, surfaced ? surfaced.id : null));
  });
  const doneRun = withRunGateLog(outcome.run, gateLog, finishedAt);
  // 2026-10-19 (Owner's 8 points, #3): a part of the pool analysis ADDS to the area — but the same crack
  // seen again in the next batch is folded into the observation already on the row (its photos join it)
  // instead of becoming a second line the inspector reads twice. A replacing scan has nothing to fold into.
  const merged = options.append === true ? mergeAppendedObservations(aiStore.get().observations.filter((s) => s.areaId === areaId), observations, finishedAt) : { fresh: observations, updated: [] as V2AiObservation[], folded: 0 };
  const kept = merged.fresh;
  // §9.1: the photos an observation rests on are PLACED on the row (2026-10-27: linked and marked 「AI 放的」;
  // 2026-10-18 … 10-26 they were proposals waiting for a tap each) — through the inspector's overrides
  // (§9.2), inside this same transaction. A folded duplicate's photos are placed through the observation
  // that absorbed them.
  const links = new Map<string, string[]>();
  // 2026-10-27 (Owner: 「AI 判缺陷即行结果默认缺陷」): the rows whose AI observation concluded a defect get 缺陷
  // prefilled — only where the inspector has not answered yet (prepareAiDefectDefaults keeps every answer).
  const defectRows = new Set<string>();
  for (const o of [...kept, ...merged.updated]) {
    if (!observationBelongsToRow(o) || o.itemId === null) continue;
    if (o.verdict === 'defect') defectRows.add(o.itemId);
    if (o.photoIds.length === 0) continue;
    links.set(o.itemId, [...(links.get(o.itemId) ?? []), ...o.photoIds]);
  }
  const autoLinks = prepareItemEvidenceSuggestions(links, finishedAt);
  // The defaults are computed on top of the placed links, so a row in both lists is written once.
  const defectDefaults = prepareAiDefectDefaults(defectRows, finishedAt, autoLinks.writes.map((w) => w.value as V2ChecklistItem));
  const prefilled = new Set(defectDefaults.writes.map((w) => (w.value as V2ChecklistItem).id));
  await persist(
    [
      { store: 'aiRuns', value: doneRun },
      ...[...kept, ...merged.updated].map((value) => ({ store: 'aiObservations' as const, value })),
      ...autoLinks.writes.filter((w) => !prefilled.has((w.value as V2ChecklistItem).id)),
      ...defectDefaults.writes
    ],
    stale.map((s) => ({ store: 'aiObservations' as const, key: s.id }))
  );
  aiStore.set((prev) => ({ ...prev, observations: prev.observations.filter((s) => !stale.some((x) => x.id === s.id)) }));
  upsertRun(doneRun);
  upsertObservations([...merged.updated, ...kept]);
  autoLinks.apply();
  defectDefaults.apply();
  setLive(areaId, null);
  const withheld = decisions.length - observations.length;
  const warnings = [...outcome.warnings, ...(withheld > 0 ? [`scopeWithheld:${withheld}`] : []), ...(merged.folded > 0 ? [`duplicatesFolded:${merged.folded}`] : [])];
  return { ok: true, run: doneRun, observations: kept, warnings };
}

// 「全部重新分析」 (Owner decision list 2026-10-19 item 29; 2026-10-28 device walkthrough A8): EVERY photo of the
// area, as its confirmation says — AI_V2_LIMITS.maxPhotos per request, one after another. The first request
// replaces the earlier observations, the next ones add to it (the pool analysis's way). Until 2026-10-28 one
// request carried the first 24 photos only and the observations of the rest were dropped. Stops at the first
// failure: the card then offers the retry of that batch, which adds.
export async function reanalyzeAreaAction(areaId: string, options: { now?: number } = {}): Promise<AnalysisOutcome> {
  const photoIds = areaScanPhotos(evidenceStore.get(), inspectionStore.get(), findingStore.get(), areaId).map((e) => e.id);
  if (photoIds.length === 0) return { ok: false, code: 'noPhotos', run: null };
  const base = options.now ?? Date.now();
  let outcome: AnalysisOutcome = { ok: false, code: 'noPhotos', run: null };
  for (let start = 0, batch = 0; start < photoIds.length; start += AI_V2_LIMITS.maxPhotos, batch += 1) {
    // a later batch always starts later, so the area's latest run is the last one sent
    outcome = await runAreaAnalysisAction(areaId, { photoIds: photoIds.slice(start, start + AI_V2_LIMITS.maxPhotos), append: start > 0, now: Math.max(base + batch, options.now === undefined ? Date.now() : 0) });
    if (!outcome.ok) return outcome;
  }
  return outcome;
}

// ---------- the inspector's choices on an UNCLASSIFIED observation (CA-5) ----------

export type FileOutcome = { ok: true } | { ok: false; reason: 'notFound' | 'locked' | 'outOfScope' | 'notUnclassified' | 'failed' };

async function saveObservation(next: V2AiObservation, extra: RecordWrite[] = []): Promise<void> {
  await persist([{ store: 'aiObservations', value: next }, ...extra]);
  upsertObservations([next]);
}

function openObservation(observationId: string): V2AiObservation | null {
  const observation = aiStore.get().observations.find((s) => s.id === observationId) ?? null;
  return observation && observationIsUnclassified(observation) ? observation : null;
}

// 归入现有检查项: the observation joins the row's ③ and its photos are proposed for the row's ② (2026-10-18:
// the AI matched the photos, so they wait for the inspector's confirmation like any AI proposal — through
// the inspector's overrides). Refused for a subject New York does not require (the gate's invariant: an
// out-of-scope subject never becomes checklist work). No row is ever created.
export async function fileObservationToItemAction(observationId: string, itemId: string, now: number = Date.now()): Promise<FileOutcome> {
  const observation = openObservation(observationId);
  if (!observation) return { ok: false, reason: 'notUnclassified' };
  const ctx = contextFor(observation.areaId);
  if (!ctx) return { ok: false, reason: 'notFound' };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, reason: 'locked' };
  if (!observationCanBeFiled(observation)) return { ok: false, reason: 'outOfScope' };
  const item = ctx.items.find((i) => i.id === itemId);
  if (!item) return { ok: false, reason: 'notFound' };
  assertInspectionUnlocked(ctx.inspection.id);
  const autoLinks = prepareItemEvidenceSuggestions(new Map([[itemId, observation.photoIds]]), now);
  await saveObservation(withObservationFiledToItem(observation, itemId, now), autoLinks.writes);
  autoLinks.apply();
  // 2026-10-27: a filed defect prefills 缺陷 on a row that has no answer yet (same rule as the area scan).
  if (observation.verdict === 'defect') {
    const defaults = prepareAiDefectDefaults(new Set([itemId]), now);
    if (defaults.writes.length > 0) {
      await persist(defaults.writes);
      defaults.apply();
    }
  }
  return { ok: true };
}

// 记为其他问题 (the ordinary case): the inspector wrote the other finding in the editor; the observation
// records where it went. The finding itself is the inspector's (created through the finding store).
export async function recordObservationAsOtherFindingAction(observationId: string, findingId: string, now: number = Date.now()): Promise<void> {
  const observation = openObservation(observationId);
  if (!observation) return;
  assertInspectionUnlocked(observation.inspectionId);
  await saveObservation(withObservationOtherFinding(observation, findingId, now));
}

// 忽略: kept in the trail, never a finding, never in the report, never in Closeout.
export async function ignoreObservationAction(observationId: string, now: number = Date.now()): Promise<void> {
  const observation = openObservation(observationId);
  if (!observation) return;
  assertInspectionUnlocked(observation.inspectionId);
  await saveObservation(withObservationNoted(observation, now));
}

// ---------- OD-13: the scope reminder and the limitation prompt ----------

export type AcceptOutcome = { ok: true; findingId: string | null; itemId: string | null } | { ok: false; reason: 'notFound' | 'locked' | 'failed' | 'outOfScope' };

// "Record as other finding" on a scope-reminder (Owner ruling OD-13; approved draft §8.2). The
// inspector writes what they saw; the record is an INFORMATIONAL other finding of the area, a
// voluntary observation — never a defect, never attached to a checklist row, never a task.
export async function recordVoluntaryObservationAction(
  observationId: string,
  input: { observation: string; title?: string },
  now: number = Date.now()
): Promise<AcceptOutcome> {
  const observation = openObservation(observationId);
  if (!observation) return { ok: false, reason: 'notFound' };
  const ctx = contextFor(observation.areaId);
  if (!ctx) return { ok: false, reason: 'notFound' };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, reason: 'locked' };
  if (!observationAllowsAction(observation, 'voluntaryObservation')) return { ok: false, reason: 'outOfScope' };
  assertInspectionUnlocked(ctx.inspection.id);
  const text = input.observation.trim();
  if (text.length === 0) return { ok: false, reason: 'failed' };
  const finding = await addFindingAction(
    {
      inspectionId: ctx.inspection.id,
      areaId: ctx.area.id,
      title: (input.title ?? observation.title).trim(),
      observation: text,
      recommendation: '',
      responsibleTrade: null,
      reportSection: ctx.area.kind,
      findingClass: 'informational',
      voluntaryObservation: true,
      evidenceIds: observation.photoIds,
      origin: 'ai',
      status: 'confirmed'
    },
    now
  );
  if (!finding) return { ok: false, reason: 'failed' };
  await saveObservation(withObservationOtherFinding(observation, finding.id, now));
  return { ok: true, findingId: finding.id, itemId: null };
}

export type LimitationOutcome = { ok: true; itemId: string } | { ok: false; reason: 'notFound' | 'locked' | 'outOfScope' | 'noTarget' | 'failed' };

// The inspector accepts a limitation prompt: the concealed PORTION of an in-scope row is recorded
// (OD-5 = A / PP-22). The row keeps the result it already has — a limitation is never a whole-row
// Unable and never a task on the obstruction, and the gate never writes an ItemStatus.
export async function acceptLimitationPromptAction(
  observationId: string,
  input: { itemId?: string; extent?: string; note?: string } = {},
  now: number = Date.now()
): Promise<LimitationOutcome> {
  const observation = openObservation(observationId);
  if (!observation || !observation.scope || observation.scope.limitation === null) return { ok: false, reason: 'notFound' };
  const ctx = contextFor(observation.areaId);
  if (!ctx) return { ok: false, reason: 'notFound' };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, reason: 'locked' };
  if (!observationAllowsAction(observation, 'limitation')) return { ok: false, reason: 'outOfScope' };
  const limitation = observation.scope.limitation;
  const itemId = input.itemId ?? limitation.targetItemId;
  if (!itemId) return { ok: false, reason: 'noTarget' };
  const item = inspectionStore.get().items.find((i) => i.id === itemId);
  if (!item || item.inspectionId !== ctx.inspection.id || !canCarryLimitedPortions(item.status)) return { ok: false, reason: 'noTarget' };
  assertInspectionUnlocked(ctx.inspection.id);
  const updated = await addLimitedPortionAction(
    itemId,
    { reasonCode: limitation.reasonCode, extent: (input.extent ?? '').trim(), note: (input.note ?? '').trim(), templateId: limitation.templateId },
    now
  );
  if (!updated) return { ok: false, reason: 'failed' };
  await saveObservation(withObservationLimitation(observation, itemId, now));
  return { ok: true, itemId };
}

// A fresh id for client-generated request ids (tests / tools).
export function newRequestId(): string {
  return newId('aireq');
}

// ---------- the facts a row's wording is written from (CA-14) ----------

// The advanced review summary of a row is the job store's (src/state/aiJobsStore.ts registers it at
// load); this module never imports that store.
let advancedSummaryProvider: ((itemId: string) => string | null) | null = null;

export function registerAdvancedSummaryProvider(provider: (itemId: string) => string | null): void {
  advancedSummaryProvider = provider;
}

export function advancedSummaryOf(itemId: string): string | null {
  return advancedSummaryProvider ? advancedSummaryProvider(itemId) : null;
}

export function rowWordingFacts(item: V2ChecklistItem, ai: AiState = aiStore.get(), evidence: EvidenceState = evidenceStore.get()): WordingFacts {
  return {
    photoNotes: item.evidenceIds.map((id) => evidence.evidence.find((e) => e.id === id)?.caption.trim() ?? '').filter((c) => c.length > 0),
    aiObservations: observationTextsOfItem(ai, item.id, { rowStatus: item.status }),
    advancedReview: advancedSummaryOf(item.id),
    structuralConcern: item.structuralConcern
  };
}

// The professional the report names for a row (rules library + the inspector's structural fact).
export function rowReferralTrade(item: Pick<V2ChecklistItem, 'templateId' | 'structuralConcern'>) {
  const rule = tradeForTemplateRow(item.templateId);
  return referralForStructuralConcern(rule === undefined ? null : rule, item.structuralConcern);
}

// ---------- report wording (Owner 2026-09-13 "报告语由 AI 写"; CA-13 / CA-14 / CA-19) ----------

// `kept` (only when > 0): rows whose sentence the inspector wrote and that the run left as written.
export type WordingOutcome = { ok: true; rows: number; kept?: number } | { ok: false; code: AiFailureCode };

export function wordingLiveOf(state: AiState, areaId: string): boolean {
  return state.wordingLive?.[areaId] === true;
}

function setWordingLive(areaId: string, live: boolean): void {
  aiStore.set((prev) => ({ ...prev, wordingLive: { ...(prev.wordingLive ?? {}), [areaId]: live } }));
}

// The report sentence(s) of an area's recorded rows, written by the model from what the inspector left
// — the note, the result, the photo notes — with the row's AI observations and advanced review as
// supporting context (CA-14) around the library's fixed facts (the check method, the professional).
// No photographs travel. The result lands on each row as its report sentence; the item sheet shows
// it and the report prints it while the facts stand.
// Owner device test 2026-09-21 — two ways the inspector can steer ONE row's sentence (both are the
// inspector's own fact sources, never an instruction to the model; the selected result rules either way):
//   fromInspectorWording  the inspector WROTE the sentence (usually in Chinese): the model renders those
//                         words as professional report language and brings both languages in line;
//   adoptObservations     the inspector read the row's AI observations and asks for the sentence to be
//                         written from them.
export interface WordingWriteOptions {
  itemIds?: readonly string[];
  now?: number;
  fromInspectorWording?: boolean;
  adoptObservations?: boolean;
}

export async function generateAreaReportWordingAction(areaId: string, options: WordingWriteOptions = {}): Promise<WordingOutcome> {
  const now = options.now ?? Date.now();
  const ctx = contextFor(areaId);
  if (!ctx) return { ok: false, code: 'notFound' };
  if (!isInspectionWorkable(ctx.inspection)) return { ok: false, code: 'locked' };
  // LAYER 2 (PP-21): the inspector's notes leave the device only where cloud AI is allowed at all.
  const policyBlock = aiPolicyBlockFor(ctx.inspection);
  if (policyBlock) return { ok: false, code: policyBlock };
  if (sessionStore.get().phase !== 'signedIn') return { ok: false, code: 'notSignedIn' };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, code: 'offline' };
  if (wordingLiveOf(aiStore.get(), areaId)) return { ok: false, code: 'busy' };
  const sameAccount = accountGuard();
  const wanted = options.itemIds ? new Set(options.itemIds) : null;
  const tEn = (key: MessageKey, params?: MessageParams) => translate('en-US', key, params);
  const tZh = (key: MessageKey, params?: MessageParams) => translate('zh-CN', key, params);
  const rows: AiV2WordingRow[] = [];
  const bases = new Map<string, string>();
  // The sentence each row carried when the request went out (see the write loop below).
  const sentAgainst = new Map<string, ItemReportSentence | null>();
  // An area-wide run (完成区域, or the area's write-the-wording) never replaces a sentence the
  // inspector wrote that still stands under the row's result — that is the inspector's text (release
  // audit 2026-10-04). Only a request on that one row does: ⑥'s AI buttons, which ask first (CA-13).
  const areaWide = wanted === null;
  let kept = 0;
  for (const item of ctx.items) {
    if (wanted && !wanted.has(item.id)) continue;
    if (item.status !== 'checkedOk' && item.status !== 'issueFound') continue;
    const facts = rowWordingFacts(item);
    const trade = rowReferralTrade(item);
    const basis = wordingBasisOf(item, facts);
    if (areaWide && item.reportSentence?.source === 'inspector' && storedSentenceCurrent(item, basis)) {
      kept += 1;
      continue;
    }
    bases.set(item.id, basis);
    sentAgainst.set(item.id, item.reportSentence);
    // The inspector's own sentence travels only when it was asked for AND still stands under the
    // row's current result, in the language the inspector wrote it last.
    const own = options.fromInspectorWording === true && item.reportSentence !== null && item.reportSentence.source === 'inspector' && storedSentenceCurrent(item, basis) ? item.reportSentence : null;
    const ownLanguage = own ? (own.edited === 'zh' ? 'zh' : 'en') : null;
    const ownText = own && ownLanguage ? (ownLanguage === 'zh' ? own.zh : own.en).trim() : '';
    rows.push({
      itemId: item.id,
      title: item.title,
      titleEn: item.templateId ? (CHECKLIST_TEMPLATE_EN_US[item.templateId]?.title ?? null) : null,
      hint: item.hint,
      result: item.status,
      inspectorNote: item.inspectorNote.trim(),
      photoNotes: [...facts.photoNotes],
      aiObservations: [...facts.aiObservations],
      advancedReview: facts.advancedReview ?? '',
      responsibleProfessional: trade ? tradeLabel(tEn, trade) : '',
      // 2026-10-19: the Chinese name too, so the zh sentence names the kind (「建议由电工评估」, never 「专业人员」)
      responsibleProfessionalZh: trade ? tradeLabel(tZh, trade) : '',
      operationalTestPerformed: item.operationalTestPerformed === true,
      supplemental: templateRowScope(item.templateId) === 'supplemental',
      ...(ownText.length > 0 ? { inspectorWording: ownText, inspectorWordingLocale: ownLanguage === 'zh' ? ('zh-CN' as const) : ('en-US' as const) } : {}),
      ...(options.adoptObservations === true && ownText.length === 0 && facts.aiObservations.length > 0 ? { adoptObservations: true } : {})
    });
  }
  if (rows.length === 0) return kept > 0 ? { ok: true, rows: 0, kept } : { ok: false, code: 'noPhotos' };
  const token = await currentAccessToken();
  if (!token || !sameAccount()) return { ok: false, code: 'notSignedIn' };
  const base = buildAreaRequestContext(ctx.inspection, ctx.area, ctx.items);
  const request: AiV2WordingRequest = {
    protocolVersion: AI_V2_PROTOCOL_VERSION,
    kind: 'reportWording',
    requestId: newId('aiw'),
    uiLocale: base.uiLocale,
    reportLocale: base.reportLocale,
    area: base.area,
    scanNote: ctx.area.scanNote,
    rows,
    inspectionId: ctx.inspection.id
  };
  setWordingLive(areaId, true);
  try {
    // LAYER 3 — THE CHOKEPOINT of this seam (PP-21, guarded by tests/guards/aiEntryPoints.guard.test.ts):
    // the gate is read again from the store immediately before the inspector's notes leave the
    // device; an unresolvable inspection is a real client. A transport that throws is a network
    // failure, never an unhandled rejection.
    const subject = inspectionStore.get().inspections.find((i) => i.id === ctx.inspection.id) ?? null;
    const wordingBlock = subject ? aiPolicyBlockFor(subject) : 'blockedRealClient';
    if (wordingBlock) return { ok: false, code: wordingBlock };
    const result = await getAiWordingTransport()(request, { accessToken: token }).catch((): null => null);
    // Account isolation (see executeRun): an answer for the account that was left is dropped whole.
    if (!sameAccount()) return { ok: false, code: 'notSignedIn' };
    if (!result) return { ok: false, code: 'network' };
    if (result.kind === 'response' && result.response.quota) applyServerQuota(result.response.quota);
    if (result.kind === 'failed') return { ok: false, code: result.code };
    if (!result.response.ok) return { ok: false, code: result.response.code };
    if (result.response.kind !== 'reportWording') return { ok: false, code: 'malformedResponse' };
    let written = 0;
    for (const sentence of result.response.rows) {
      const basis = bases.get(sentence.itemId);
      if (!basis) continue;
      // The wording guard and the inspection standard apply to the written sentence like to any
      // report-bound text: the offending sentence goes, the rest stays.
      const en = stripInvasiveInstructions(stripProhibitedSentences(sentence.en, 'report').text).trim();
      const zh = stripInvasiveInstructions(stripProhibitedSentences(sentence.zh, 'report').text).trim();
      if (en.length === 0 && zh.length === 0) continue;
      // The inspector wrote this row's sentence while the request was out: their words win.
      const before = sentAgainst.get(sentence.itemId) ?? null;
      const stored = inspectionStore.get().items.find((i) => i.id === sentence.itemId)?.reportSentence ?? null;
      if (stored?.source === 'inspector' && (before?.source !== 'inspector' || before.at !== stored.at)) {
        kept += 1;
        continue;
      }
      await setItemReportSentenceAction(sentence.itemId, { en, zh, source: 'ai', basis, at: now }, now);
      written += 1;
    }
    return kept > 0 ? { ok: true, rows: written, kept } : { ok: true, rows: written };
  } finally {
    setWordingLive(areaId, false);
  }
}
